import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import { Capacitor } from '@capacitor/core'
import { QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App.tsx'
import { hydrateSecureStorage } from '@pp/shared/platform/storage'
import { nativeAppMode } from '@pp/shared/platform/platform'
import { configureBiometric } from '@pp/shared/auth/biometricAuth'
import { configurePush } from '@pp/shared/push/push'
import { registerDevice, unregisterDevice } from './api/devicesApi'
import { queryClient } from './lib/queryClient'
import { initSentry, markReloadingForStaleChunk } from './lib/sentry'

// Self-heal chunk-load failures after a deploy: when a lazy route's hashed
// chunk no longer exists (stale index.html vs new server assets), Vite
// fires vite:preloadError before the import rejects into the error
// boundary. One reload fetches the fresh index.html (served no-cache) and
// recovers. The sessionStorage guard prevents a reload loop when the
// server itself is broken; it clears shortly after a successful load.
const PRELOAD_RELOAD_GUARD = 'pp.reloadedForPreloadError';
window.addEventListener('vite:preloadError', (event) => {
  if (sessionStorage.getItem(PRELOAD_RELOAD_GUARD)) return;
  event.preventDefault();
  // preventDefault makes Vite resolve the import with undefined, so the
  // lazy() wrappers in App.tsx throw before the reload takes effect.
  // Tell Sentry to ignore that: it's noise from a page already on its
  // way out, and link scanners detonating emailed URLs produce a lot of it.
  markReloadingForStaleChunk();
  sessionStorage.setItem(PRELOAD_RELOAD_GUARD, '1');
  window.location.reload();
});
window.setTimeout(() => sessionStorage.removeItem(PRELOAD_RELOAD_GUARD), 15_000);

// Scope this app's biometric keychain items under the shell's bundle id.
// The same bundle ships in two Capacitor shells — Dispatch (field) and the
// Customer Portal — distinguished at build time via VITE_NATIVE_APP.
const isPortalShell = nativeAppMode() === 'portal';
configureBiometric({
  keychainServer: isPortalShell
    ? 'com.packetprospector.portal'
    : 'com.packetprospector.field',
});

// Tag push registrations with the shell's audience so the backend routes
// them through the right Notification Hub: 0 = Field (Dispatch), 1 = Customer
// (portal shell).
configurePush({ audience: isPortalShell ? 1 : 0, registerDevice, unregisterDevice });

// Crash reporting must initialize before anything else so an early
// boot error (failing import, broken plugin, status-bar setup) still
// reaches Sentry. The function is a hard no-op when VITE_SENTRY_DSN
// is unset (local dev, PRs, CI without the secret), so it's safe to
// run unconditionally.
initSentry();

// The same Vite bundle ships as a web PWA AND inside the Capacitor native
// shell. The PWA's service worker must NOT register inside the native shell:
// Capacitor loads the app from capacitor://localhost, and a service worker
// registered there can hold onto stale caches that the native code can't
// invalidate (Settings → "clear app data" is the only escape hatch).
//
// Web: VitePWA registers normally, install prompt works, offline asset cache
// is active.
// Native (iOS / Android): we skip registration entirely. Capacitor handles
// offline asset loading directly from the bundled .ipa / .apk.
const isNativeShell = Capacitor.isNativePlatform();

// Tag the document for native-only CSS hooks. Currently used by index.css
// to lock the document scroll inside the Capacitor WebView — without this,
// iOS rubber-band overscroll drags the entire app (including the fixed
// bottom nav) past the viewport edges. Web users keep normal body scroll.
if (isNativeShell) {
  document.documentElement.classList.add('native-shell');
}

// White-label branding for the portal shell: tenant builds bake in
// VITE_PORTAL_TENANT (the tenant's subdomain) and we fetch that tenant's
// live name / logo / colors at boot, so the login screen is branded before
// any sign-in and a tenant rebrand never requires an app resubmission.
// Fire-and-forget: the store applies CSS vars when the fetch lands, and the
// UI renders unbranded defaults until then (or forever, if it fails).
const portalTenant = import.meta.env.VITE_PORTAL_TENANT;
if (isPortalShell && portalTenant) {
  void import('./contexts/portalBrandStore').then(({ usePortalBrandStore }) =>
    usePortalBrandStore.getState().loadBrand(portalTenant)
  );
}

/**
 * Native shell startup: configure the iOS / Android status bar so the field
 * app's header looks like part of one continuous surface instead of having a
 * mismatched-color strip at the top. Failures here are non-fatal — if the
 * plugin isn't available (e.g. running the same bundle on web), we just skip.
 */
if (isNativeShell) {
  void (async () => {
    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      // The field app's header is white in light mode and dark gray in dark
      // mode. Light status-bar text (Style.Light) reads as dark icons on iOS,
      // which fits a light header. We'll revisit if/when we add a true dark
      // theme toggle on the native shell.
      await StatusBar.setStyle({ style: Style.Light });
    } catch (err) {
      console.warn('Status bar init failed:', err);
    }

    // Initialize Google / Apple SSO with the env-configured client IDs. No-op
    // when the plugin's already been initialized. Failures here log but
    // don't block the rest of the app — buttons gate on isConfigured() too.
    try {
      const { initSocialLogin } = await import('@pp/shared/auth/socialLogin');
      await initSocialLogin();
    } catch (err) {
      console.warn('Social login init failed:', err);
    }
  })();
}

// Block Mapbox telemetry by intercepting requests to events.mapbox.com
// See: https://github.com/mapbox/mapbox-gl-js/issues/13226
const originalFetch = window.fetch;
window.fetch = function(...args) {
  const url = args[0];
  if (typeof url === 'string' && url.includes('events.mapbox.com')) {
    return Promise.resolve(new Response('{}', { status: 200 }));
  }
  return originalFetch.apply(this, args);
};

// Check if we're on an auth route - these should NEVER be handled by service worker
const isAuthRoute = window.location.pathname.startsWith('/auth/') ||
                    window.location.pathname === '/login' ||
                    window.location.pathname === '/register' ||
                    window.location.pathname === '/forgot-password' ||
                    window.location.pathname === '/reset-password';

/**
 * Initialize the React app.
 * On auth routes, we MUST unregister service workers and clear caches BEFORE rendering
 * to prevent stale cached content from being displayed.
 */
async function initializeApp() {
  // Refill localStorage from the platform secure store on native. Must happen
  // before render so the sync `storage.get(JWT_TOKEN, null)` reads inside
  // contexts/store.ts and api/client.ts see the right value on cold start.
  // No-op on web.
  await hydrateSecureStorage();

  if (isNativeShell) {
    // Native shell — skip every service-worker code path.
  } else if (isAuthRoute && 'serviceWorker' in navigator) {
    // CRITICAL: Unregister service workers and clear caches BEFORE rendering
    // This prevents stale cached content on login/register pages
    // which can cause the Google login button to not appear until hard refresh
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));

      // Also clear the service worker caches to ensure fresh content
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames
            .filter(name => name.includes('workbox') || name.includes('api-cache'))
            .map(name => caches.delete(name))
        );
      }
    } catch (error) {
      console.warn('Failed to clear service worker caches:', error);
    }
  } else if (!isAuthRoute) {
    // Register service worker with auto-reload on new version
    // This ensures users always get the latest version without manual refresh
    registerSW({
      onNeedRefresh() {
        // New content available - reload to get the latest version
        window.location.reload();
      },
      onOfflineReady() {
        // App is ready for offline use
      },
      onRegisteredSW(_swUrl, registration) {
        // Check for updates every 5 minutes
        if (registration) {
          setInterval(() => {
            registration.update();
          }, 5 * 60 * 1000);
        }
      },
    });
  }

  // Now render the app - service workers are cleared on auth routes
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  );
}

// Start app initialization
initializeApp();
